import firebase from 'firebase';
import {Timestamp, DocumentReference} from '@firebase/firestore-types';
const firestore = firebase.firestore;

/**
 * Firestore database hooks and utilities for managing timestamps.
 * This module provides generic functions to standardize CREATED_AT and UPDATED_AT
 * timestamp management across all Firestore operations.
 *
 * @module db-hooks
 */
/**
 * @description Extends a data object with CREATED_AT timestamp only.
 * Used for creating new documents.
 *
 * @template T - The type of the data object
 * @param {T} data - The data object to extend with creation timestamp
 * @returns {T & { CREATED_AT: Timestamp }} The data object with CREATED_AT timestamp
 *
 * @example
 * const campaignData = { name: 'Summer Sale', budget: 1000 };
 * const dataWithTimestamps = extendWithCreatedAt(campaignData);
 * // Result: { name: 'Summer Sale', budget: 1000, CREATED_AT: Timestamp }
 */
export function extendWithCreatedAt<T extends object>(
  data: T,
): T & { CREATED_AT: Timestamp } {
  const now = firestore.Timestamp.now();
  return {
    ...data,
    CREATED_AT: now,
  };
}

/**
 * @description Extends a data object with only UPDATED_AT timestamp.
 * Used for updating existing documents.
 *
 * @template T - The type of the data object
 * @param {T} data - The data object to extend with update timestamp
 * @returns {T & { UPDATED_AT: Timestamp }} The data object with UPDATED_AT timestamp
 *
 * @example
 * const updateData = { budget: 1500 };
 * const dataWithTimestamp = extendWithUpdatedAt(updateData);
 * // Result: { budget: 1500, UPDATED_AT: Timestamp }
 */
export function extendWithUpdatedAt<T extends object>(
  data: T,
): T & { UPDATED_AT: Timestamp } {
  const now = firestore.Timestamp.now();
  return {
    ...data,
    UPDATED_AT: now,
  };
}



/**
 * @description Updates an existing document by reference using .update().
 * Adds only UPDATED_AT timestamp.
 *
 * @template T - The type of the document data
 * @param {DocumentReference} docRef - The Firestore document reference
 * @param {T} data - The data to update
 * @returns {Promise<void>} A promise that resolves when the update is complete
 */
export async function updateDocumentRef<T extends object>(
  docRef: DocumentReference,
  data: T,
): Promise<void> {
  const updatedData = extendWithUpdatedAt(data);
  return docRef.update(updatedData);
}

/**
 * @description Writes a document by reference using .set() (overwrites if it exists, creates if missing).
 * Updates UPDATED_AT for updates; when creating, also sets CREATED_AT. To avoid creating,
 * use update() or perform an exists check first.
 *
 * @template T - The type of the document data
 * @param {DocumentReference} docRef - The Firestore document reference
 * @param {T} data - The data to set
 * @returns {Promise<void>} A promise that resolves when the set operation is complete
 */
export async function setDocumentRef<T extends object>(
  docRef: DocumentReference,
  data: T,
): Promise<void> {
  return docRef.set(extendWithUpdatedAt(data));
}

/**
 * @description Creates a new document by reference using .set().
 * Adds only CREATED_AT timestamp.
 *
 * @template T - The type of the document data
 * @param {DocumentReference} docRef - The Firestore document reference
 * @param {T} data - The data to create
 * @returns {Promise<void>} A promise that resolves when the document is created
 */
export async function createDocumentRef<T extends object>(
  docRef: DocumentReference,
  data: T,
): Promise<void> {
  const newData = extendWithCreatedAt(data);
  return docRef.set(newData);
}
